// BookingSuccess.js - Booking confirmation page
// Shown after payment is successfully completed
// Gives the customer a warm confirmation and next steps
// Auto-redirects to dashboard with ?booked=true after 10 seconds

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

function BookingSuccess() {
  const navigate = useNavigate();
  const location = useLocation();

  // Get booking details passed from the payment page
  const { serviceType, lovedOneName, preferredDate } = location.state || {};

  // State for countdown timer that auto redirects to dashboard
  const [countdown, setCountdown] = useState(10);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    // Fetch user's first name to personalize the message
    const user = auth.currentUser;
    if (user) {
      const fetchUser = async () => {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        }
      };
      fetchUser();
    }

    // Countdown timer — redirects to dashboard after 10 seconds
    // ?booked=true triggers the subscription upsell popup on the dashboard
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/dashboard?booked=true');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="success-page">

      {/* Logo */}
      <a href="/" className="auth-logo">Evermark Memorial</a>

      {/* ========== SUCCESS CARD ========== */}
      <div className="success-card">

        {/* Animated checkmark */}
        <div className="success-icon">✅</div>

        {/* Personalized headline */}
        <h2>
          {userData ? `Thank you, ${userData.firstName}.` : 'Your visit is booked.'}
        </h2>

        {/* Emotional subheadline */}
        <p className="success-subtitle">
          {lovedOneName
            ? `We'll make sure ${lovedOneName} is honored with care and respect.`
            : 'We\'ll make sure your loved one is honored with care and respect.'}
        </p>

        {/* ========== BOOKING DETAILS SUMMARY ========== */}
        {(serviceType || preferredDate) && (
          <div className="success-details">
            {serviceType && (
              <div className="success-detail-row">
                <span className="success-detail-label">Service</span>
                <span className="success-detail-value">{serviceType}</span>
              </div>
            )}
            {preferredDate && (
              <div className="success-detail-row">
                <span className="success-detail-label">Requested Date</span>
                <span className="success-detail-value">{preferredDate}</span>
              </div>
            )}
            <div className="success-detail-row">
              <span className="success-detail-label">Status</span>
              <span className="success-detail-value success-status">
                Finding a caretaker near you
              </span>
            </div>
          </div>
        )}

        {/* ========== WHAT HAPPENS NEXT ========== */}
        <div className="success-next-steps">
          <h4>What happens next:</h4>
          <div className="success-step">
            <span className="success-step-icon">🔍</span>
            <span>We match you with a trusted local caretaker in your area</span>
          </div>
          <div className="success-step">
            <span className="success-step-icon">🌿</span>
            <span>Your caretaker visits the grave and tends to it with care</span>
          </div>
          <div className="success-step">
            <span className="success-step-icon">📸</span>
            <span>Before and after photos are uploaded to your dashboard</span>
          </div>
          <div className="success-step">
            <span className="success-step-icon">💌</span>
            <span>You receive a notification when your visit is complete</span>
          </div>
        </div>

        {/* Go to dashboard button — includes ?booked=true to trigger upsell */}
        <button
          className="btn-primary btn-full"
          onClick={() => navigate('/dashboard?booked=true')}
        >
          Go to My Dashboard
        </button>

        {/* Auto redirect countdown */}
        <p className="success-countdown">
          Redirecting to your dashboard in {countdown} seconds...
        </p>

      </div>

    </div>
  );
}

export default BookingSuccess;
